import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { Model, ReasoningEffort } from '../types';

import { ConversationTool } from './ConversationTool';

import type { AppDispatch } from 'js/src/libs/RootState';

export const capabilityCheckSchema = z.object({
    request: z.string().describe('The request or action to check'),
});
export type CapabilityCheckArgs = z.infer<typeof capabilityCheckSchema>;

interface CapabilityInfo {
    canDo: boolean;
    explanation: string;
    howToInstructions?: string;
}

export class CapabilityCheckTool extends ConversationTool<typeof capabilityCheckSchema> {
    public override readonly childModel = Model.GPT_5_6_LUNA;
    public override readonly childReasoningEffort = ReasoningEffort.LOW;

    protected override readonly schema = capabilityCheckSchema;
    protected override readonly toolName = TOOL_NAMES.checkCapability;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription =
        'Check if a requested action is possible in Lumen5 and/or the Chat system. Use this when a user asks for something that might not be available or possible.';

    private capabilities: Map<string, CapabilityInfo>;

    constructor(args: { luminary: Luminary; dispatch: AppDispatch }) {
        super(args);
        this.capabilities = this.buildCapabilityMap();
    }

    public override getToolDisplayName(): string {
        return 'Checking capabilities';
    }

    public get statusIcon(): string {
        return 'help';
    }

    async execute(
        args: CapabilityCheckArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const request = args.request.toLowerCase();

        // First, check our predefined capabilities
        for (const [pattern, capability] of this.capabilities.entries()) {
            if (this.matchesPattern(request, pattern)) {
                return {
                    result: this.formatCapabilityResponse(capability),
                    isSuccess: true,
                };
            }
        }

        // If no predefined match, use LLM to analyze the request
        return this.checkCapabilityWithLLM(args.request, context);
    }

    private formatCapabilityResponse(capability: CapabilityInfo): string {
        if (capability.canDo) {
            if (capability.howToInstructions) {
                return `This is possible in Lumen5! Tell the user this (include markdown verbatim):\n\n${capability.explanation}\n\n**How to do it:**\n${capability.howToInstructions}`;
            } else {
                return `This is possible! Tell the user this (include markdown verbatim):\n\n${capability.explanation}`;
            }
        } else {
            return `This is not possible in Lumen5. Tell the user this (include markdown verbatim):\n\n${capability.explanation}${capability.howToInstructions ? `\n\n**Alternative:** ${capability.howToInstructions}` : ''}`;
        }
    }

    private async checkCapabilityWithLLM(
        request: string,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        try {
            const capabilityPrompt = this.buildCapabilityPrompt(request);
            const conversation = this.createChildConversationForToolCall(context.toolCallId, {
                systemMessage: undefined,
                tools: [],
                task: 'capability_check_tool',
                requiresVideoState: false,
            });

            conversation.addUserMessage(capabilityPrompt);

            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
            });
            if (!result.success) {
                return {
                    result: `Error during capability analysis: ${result.message || 'no result was returned. Try again in a moment.'}`,
                    isSuccess: false,
                };
            }

            return {
                result: result.message || 'No analysis returned.',
                isSuccess: true,
            };
        } catch (error) {
            return {
                result: `**I'm not sure about this capability.**\n\nI couldn't find specific information about "${request}" in my capability database and couldn't analyze it further. This might be something that's possible but not commonly requested.\n\nYou can try asking me to do it directly, or contact support for clarification about this specific feature.`,
                isSuccess: false,
            };
        }
    }

    private buildCapabilityPrompt(request: string): string {
        const impossibleFeatures: string[] = [];
        const possibleButNotChatFeatures: string[] = [];

        // Build lists from the capability map
        for (const [, capability] of this.capabilities.entries()) {
            if (!capability.canDo) {
                impossibleFeatures.push(`- ${capability.explanation}`);
            } else if (capability.howToInstructions) {
                possibleButNotChatFeatures.push(
                    `- ${capability.explanation} (${capability.howToInstructions})`,
                );
            }
        }

        return `Analyze whether the following request is possible in Lumen5, a video creation platform with an AI chat assistant.

User Request: "${request}"

Context about Lumen5 capabilities:

THINGS THAT ARE NOT POSSIBLE in Lumen5:
${impossibleFeatures.join('\n')}

THINGS POSSIBLE IN LUMEN5 BUT NOT THROUGH CHAT:
${possibleButNotChatFeatures.join('\n')}

Please respond with:
1. POSSIBLE: if possible (and whether through Chat or manual steps)
2. IMPOSSIBLE: if not possible in Lumen5
3. A clear explanation
4. Instructions on how to do it (if possible)`;
    }

    private matchesPattern(request: string, pattern: string): boolean {
        const keywords = pattern.split('|');
        return keywords.some((keyword) => request.includes(keyword.toLowerCase()));
    }

    private buildCapabilityMap(): Map<string, CapabilityInfo> {
        const capabilities = new Map<string, CapabilityInfo>();

        // Things that aren't possible in Lumen5
        capabilities.set('animated video|animation|animate|cartoon', {
            canDo: false,
            explanation:
                'Lumen5 currently does not support creating animated videos. Lumen5 videos are made of stock media or photos & video clips you upload.',
            howToInstructions:
                'If you would like to generate AI images you can use the AI Media tab.',
        });
        capabilities.set('fetch images from web|download images|web images|internet images', {
            canDo: false,
            explanation:
                'Lumen5 cannot fetch or download images from the internet. You need to upload your own media files.',
            howToInstructions: 'Upload images through the Media tab.',
        });
        capabilities.set('switch template|change template|template switch', {
            canDo: false,
            explanation:
                'You cannot switch to a different template once a video is created. The template defines the fundamental designs of the video.',
            howToInstructions:
                'You can switch designs for a scene by clicking "Swap design" next to each scene.',
        });
        capabilities.set(
            'import from google docs|import from word|import from dropbox|import from onedrive|import from google drive|import from cloud storage',
            {
                canDo: false,
                explanation:
                    'Lumen5 cannot import content from sites like Google Docs or Microsoft Word, or from cloud storage services like OneDrive, Google Drive, or DropBox.',
                howToInstructions:
                    'Copy and paste your text directly into Lumen5, or export your document as a PDF and upload it.',
            },
        );
        capabilities.set('custom transitions|add transitions|transition effects', {
            canDo: false,
            explanation: 'Lumen5 does not support custom transitions between scenes.',
        });
        capabilities.set('picture in picture|pip|multiple videos|overlay video', {
            canDo: false,
            explanation:
                'Lumen5 does not support picture-in-picture effects or overlaying multiple videos in a single scene.',
            howToInstructions: 'Create separate scenes for each video you want to include.',
        });
        capabilities.set('multi-track audio|multiple audio tracks|audio layers', {
            canDo: false,
            explanation:
                'Lumen5 does not support multiple audio tracks or audio layering. You can only have one background music track and one voiceover track.',
        });

        // Things that CAN be done in Lumen5 but not by Chat
        capabilities.set('add voiceover|add voice|add narration', {
            canDo: true,
            explanation: 'Adding voiceover cannot be done through the Agent tab.',
            howToInstructions: 'Go to the Voice tab to upload or record a voiceover.',
        });
        capabilities.set('timing|duration|shorter|faster|longer|time', {
            canDo: false,
            explanation: 'Timing adjustments cannot be made through the Agent tab.',
            howToInstructions:
                'Make scenes longer or shorter by adjusting the duration to the right of the scene.',
        });
        capabilities.set('change music|add music|background music|soundtrack', {
            canDo: true,
            explanation:
                'Adding and changing background music cannot be done through the Agent tab.',
            howToInstructions:
                'Go to the Music tab to search and select background music for your video.',
        });
        capabilities.set('add scene group|scene groups|group scenes', {
            canDo: true,
            explanation: 'Scene groups cannot be added through the Agent tab.',
            howToInstructions:
                'Add a new scene group by clicking the green "+" button underneath a scene.',
        });
        capabilities.set('edit transcript|change transcript|modify transcript', {
            canDo: true,
            explanation: 'Transcript editing cannot be done through the Agent tab.',
            howToInstructions: 'Go to the Transcript on the right to directly edit the transcript.',
        });
        capabilities.set('switch voice|change voice|different voice|voice settings', {
            canDo: true,
            explanation: 'Voice settings cannot be modified through the Agent tab.',
            howToInstructions:
                'Go to the Transcript on the right and use the voice settings to select different voices or adjust speed.',
        });
        capabilities.set('upload media|upload images|upload videos|add custom media', {
            canDo: true,
            explanation: 'Media uploads cannot be done through the Agent tab.',
            howToInstructions:
                'Go to the Media tab and click the upload button to add your own images or video clips.',
        });
        capabilities.set(
            'generate ai images|ai media|generate media|create images|ai generated images',
            {
                canDo: true,
                explanation: 'AI images can be generated through the Agent tab using text prompts.',
                howToInstructions: `Use the ${TOOL_NAMES.generateAiMedia} tool or ask me to generate an image with a descriptive prompt.`,
            },
        );
        capabilities.set('remove background|background removal|cut out|transparent background', {
            canDo: true,
            explanation: 'Image backgrounds can be removed through the Agent tab.',
            howToInstructions: `Use the ${TOOL_NAMES.generateAiMedia} tool with operation "remove_background", or ask me to remove the background of an image.`,
        });
        capabilities.set('download|export|save|publish', {
            canDo: true,
            explanation: 'Video downloads cannot be initiated through the Agent tab.',
            howToInstructions:
                'Click the "Download" button at the top right to render and save your video.',
        });
        capabilities.set('share video|share to social media', {
            canDo: true,
            explanation: 'Video sharing cannot be done through the Agent tab.',
            howToInstructions:
                'Click the "Download" button at the top right to get a share link for your video.',
        });
        capabilities.set('video resolution|change video quality|video settings', {
            canDo: true,
            explanation: 'Video resolution cannot be modified through the Agent tab.',
            howToInstructions:
                'Click the "Download" button at the top right to select your desired video resolution (up to 1080p).',
        });
        return capabilities;
    }
}
